/* [PATCH pn-prenos-kn21 v1] [PATCH pn-prenos-kn21 v2] Potni nalogi → Prenos v knjigovodstvo (Vasco KN-21 temeljnica). Samo admin.
   API: /potni-nalogi/{cid}/prenos/{seznam,nastavitve,predogled,kn21,odznaci} (api/pn_prenos.py).
   Odločitve Janeza 30.09.2026: D1–D8, O1, O2; v2: okna namesto confirm, nastavitve v oknu + »Ponastavi na privzeto«,
   enotna temeljnica (ime določi uporabnik), filter številka od–do, stran čez celo širino. */
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { ArrowRightLeft, Download, RotateCcw, RefreshCw, Save, AlertTriangle, Settings, X, Eye, Undo2, Archive, FolderOpen } from 'lucide-react'; // [PATCH pn-arhiv-fe v1] Archive, FolderOpen
import { api } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { VRSTE } from './PotniNalogiPage';

const polje = 'w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400';
const konto = 'w-28 px-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/30';
const gumb = 'inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-sm font-medium disabled:opacity-50 whitespace-nowrap';
const gumbSek = gumb + ' border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-blue-400';
const eur = (v: any) => Number(v || 0).toLocaleString('sl-SI', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const datum = (s?: string | null) => (s ? s.slice(8, 10) + '.' + s.slice(5, 7) + '.' + s.slice(0, 4) : '');
const tabBtn = (a: boolean) => `px-4 py-2 rounded-xl text-sm font-medium ${a ? 'bg-blue-600 text-white' : 'bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300'}`;
const stNum = (s: any) => { const m = String(s || '').match(/(\d+)\s*$/); return m ? parseInt(m[1], 10) : NaN; };
const ZVEZDICA: Record<string, string> = { DNEVNICE_SI: '*', DNEVNICE_TUJINA: '*', KILOMETRINA: '*', STROSKI_PODJETJE: '**' }; // [PATCH pn-prenos-kn21 v3]

async function napakaIzBloba(e: any): Promise<any> {
  const d = e?.response?.data;
  if (d instanceof Blob) { try { return JSON.parse(await d.text()); } catch { return null; } }
  return d;
}

function Okno({ naslov, onZapri, children, noga, siroko }: { naslov: string; onZapri: () => void; children: React.ReactNode; noga?: React.ReactNode; siroko?: boolean }) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onZapri(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onZapri]);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={onZapri}>
      <div className={'w-full rounded-2xl bg-white dark:bg-gray-900 shadow-xl flex flex-col max-h-[92vh] ' + (siroko ? 'max-w-6xl' : 'max-w-lg')} onMouseDown={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="text-base font-semibold">{naslov}</h2>
          <button onClick={onZapri} aria-label="Zapri" className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-5 py-4 overflow-auto">{children}</div>
        {noga && <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-800 flex flex-wrap items-center gap-2 justify-end">{noga}</div>}
      </div>
    </div>);
}

function TabelaKnjizb({ vrstice }: { vrstice: any[] }) {
  const b = vrstice.filter(x => x.stran === 'breme').reduce((s, x) => s + Number(x.znesek ?? x.z), 0);
  const d = vrstice.filter(x => x.stran === 'dobro').reduce((s, x) => s + Number(x.znesek ?? x.z), 0);
  const ok = Math.abs(b - d) < 0.005;
  return (<>
    <table className="w-full text-sm">
      <thead><tr className="text-xs text-gray-400"><th className="text-left font-normal">Konto</th><th className="text-left font-normal">Opis</th><th className="text-right font-normal">Breme</th><th className="text-right font-normal">Dobro</th></tr></thead>
      <tbody>{vrstice.map((x, i) => (
        <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
          <td className="py-1 font-mono">{x.konto}</td><td className="text-xs text-gray-600 dark:text-gray-300">{x.opis}{x.partner ? ` · partner ${x.partner}` : ''}</td>
          <td className="text-right font-mono tabular-nums">{x.stran === 'breme' ? eur(x.znesek ?? x.z) : ''}</td>
          <td className="text-right font-mono tabular-nums">{x.stran === 'dobro' ? eur(x.znesek ?? x.z) : ''}</td></tr>))}</tbody>
    </table>
    <div className={'text-xs mt-2 ' + (ok ? 'text-green-600' : 'text-red-600')}>{ok ? 'Breme = dobro' : 'Ni v ravnotežju'} · {eur(b)} / {eur(d)}</div>
  </>);
}

/* Primer knjiženja — ista pravila kot services/izvoz/vasco_pn.py (za prikaz, knjiži backend) */
function primer(n: any) {
  const v = n?.vrstice || {};
  const vr: any[] = [];
  let obv = 0;
  const dodaj = (k: string, stran: string, z: number, opis: string) => vr.push({ konto: k || '—', stran, z, opis });
  if (!v.DNEVNICE_SI?.ne_prenasa) { dodaj(v.DNEVNICE_SI?.konto, 'breme', 25.03, 'Dnevnice – Slovenija'); obv += 25.03; }
  if (!v.KILOMETRINA?.ne_prenasa) { dodaj(v.KILOMETRINA?.konto, 'breme', 43, 'Kilometrina'); obv += 43; }
  if (!v.STROSKI_POVRNE?.ne_prenasa) { dodaj(n?.stroski_vrste?.parkirnina || v.STROSKI_POVRNE?.konto, 'breme', 200, 'Parkirnina (povrne se)'); obv += 200; }
  if (!v.STROSKI_PODJETJE?.ne_prenasa) {
    dodaj(n?.stroski_vrste?.cestnina || v.STROSKI_PODJETJE?.konto, 'breme', 5, 'Cestnina (plačalo podjetje)');
    dodaj(v.STROSKI_PODJETJE?.konto_dobro, 'dobro', 5, 'Cestnina – protiknjižba');
  }
  if (obv > 0) dodaj(v.OBVEZNOST?.konto, 'dobro', obv, 'Obveznost do zaposlenega' + (n?.s_partnerjem ? ' (partner)' : ''));
  if (!n?.kumulativno) return vr;
  const m: Record<string, any> = {};
  vr.forEach(x => { const k = x.konto + '|' + x.stran; m[k] = m[k] ? { ...m[k], z: m[k].z + x.z, opis: 'skupaj' } : { ...x }; });
  return Object.values(m);
}

function Stikalo({ vkl, onChange, naslov, opis }: { vkl: boolean; onChange: (v: boolean) => void; naslov: string; opis?: string }) {
  return (
    <label className="flex items-start gap-3 py-2 cursor-pointer">
      <input type="checkbox" className="mt-1" checked={vkl} onChange={e => onChange(e.target.checked)} />
      <span><span className="text-sm font-medium">{naslov}</span>{opis && <span className="block text-xs text-gray-500">{opis}</span>}</span>
    </label>);
}

/* [PATCH pn-konti-stroskov-fe v1] vsaka vrsta stroška ima svoj konto; prazno se predizpolni s kontom vrstice »Stroški – povrne se« (= dosedanje knjiženje) */
function predizpolni(n: any) {
  if (!n) return n;
  const sv = { ...(n.stroski_vrste || {}) };
  const os = String(n.vrstice?.STROSKI_POVRNE?.konto || '').trim();
  (VRSTE as any[]).forEach((x: any) => { if (!String(sv[x[0]] || '').trim()) sv[x[0]] = os; });
  return { ...n, stroski_vrste: sv };
}

function NastavitveOkno({ cid, onZapri }: { cid: string; onZapri: () => void }) {
  const [n, setN] = useState<any>(null);
  const [privzete, setPrivzete] = useState<any>(null);
  const [opis, setOpis] = useState<any[]>([]);
  const [shranjujem, setShranjujem] = useState(false);
  useEffect(() => {
    api.get(`/potni-nalogi/${cid}/prenos/nastavitve`).then(r => { setN(predizpolni(r.data.nastavitve)); setPrivzete(r.data.privzete); setOpis(r.data.vrstice); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Nastavitev ni mogoče naložiti'));
  }, [cid]);
  const pr = useMemo(() => primer(n), [n]);
  const vrst = (k: string, p: string, val: any) => setN((s: any) => ({ ...s, vrstice: { ...s.vrstice, [k]: { ...s.vrstice[k], [p]: val } } }));
  const shrani = async () => {
    setShranjujem(true);
    try { await api.put(`/potni-nalogi/${cid}/prenos/nastavitve`, n); toast.success('Nastavitve prenosa shranjene'); onZapri(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || 'Shranjevanje ni uspelo'); }
    finally { setShranjujem(false); }
  };
  const noga = (<>
    <button className={gumbSek + ' mr-auto'} disabled={!privzete} onClick={() => { setN(predizpolni(JSON.parse(JSON.stringify(privzete)))); toast('Privzete nastavitve so naložene — shrani, da veljajo', { icon: '↩️' }); }}><Undo2 className="w-4 h-4" />Ponastavi na privzeto</button>
    <button className={gumbSek} onClick={onZapri}>Prekliči</button>
    <button className={gumb + ' bg-blue-600 text-white'} disabled={!n || shranjujem || (VRSTE as any[]).some((x: any) => !String(n?.stroski_vrste?.[x[0]] || '').trim())} onClick={shrani}><Save className="w-4 h-4" />Shrani</button>
  </>);
  return (
    <Okno naslov="Nastavitve prenosa · Vasco KN-21" onZapri={onZapri} noga={noga} siroko>
      {!n ? <div className="py-10 text-center text-gray-400 text-sm">Nalagam …</div> : (
        <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)] gap-6">
          <div className="space-y-5">
            <section>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Temeljnica</div>
              <div className="flex items-center gap-3 py-2"><span className="text-sm w-40">Simbol v Vascu</span>
                <input className={konto + ' w-20'} inputMode="numeric" value={n.simbol ?? ''} onChange={e => setN({ ...n, simbol: e.target.value.replace(/\D/g, '') })} /></div>
              <Stikalo vkl={!!n.enotna} onChange={v => setN({ ...n, enotna: v })} naslov="Vsi izbrani nalogi v eno temeljnico"
                opis="Ob prenosu vpišeš ime temeljnice (do 14 znakov) in datum knjiženja. Izklopljeno: vsak nalog je svoja temeljnica s številko naloga." />
              <Stikalo vkl={!!n.kumulativno} onChange={v => setN({ ...n, kumulativno: v })} naslov="Ena vrstica na konto (kumulativno)"
                opis={n.enotna ? 'Združi vrstice istega konta čez vse naloge v temeljnici.' : 'Združi vrstice istega konta znotraj naloga.'} />
            </section>
            <section>
              <div className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Kam gre kateri znesek</div>{/* [PATCH pn-nastavitve-gnezdo-fe v1] obveznost na vrhu, stroški kot skupina [PATCH pn-konti-stroskov-fe v1] */}
              <div className="rounded-xl border border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                <div className="flex flex-wrap items-center gap-3 px-3 py-2.5 bg-gray-50/60 dark:bg-gray-800/30">
                  <div className="flex-1 min-w-[12rem]"><div className="text-sm">Obveznost do zaposlenega</div>
                    <label className="flex items-center gap-1.5 mt-1 text-xs text-gray-500"><input type="checkbox" checked={!!n.s_partnerjem} onChange={e => setN({ ...n, s_partnerjem: e.target.checked })} />s partnerjem (šifra partnerja pri zaposlenem)</label></div>
                  <span className="text-xs px-2 py-0.5 rounded-md bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-300">dobro</span>
                  <input className={konto} maxLength={8} value={n.vrstice.OBVEZNOST?.konto || ''} onChange={e => vrst('OBVEZNOST', 'konto', e.target.value.trim())} />
                  <span className="w-32" />
                </div>
                {opis.filter(o => o.stran === 'breme' && !o.kljuc.startsWith('STROSKI_')).map(o => {
                  const ne = !!n.vrstice[o.kljuc]?.ne_prenasa;
                  return (
                    <div key={o.kljuc} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                      <div className="flex-1 min-w-[12rem]"><div className={'text-sm ' + (ne ? 'text-gray-400' : '')}>{o.naziv}</div></div>
                      <span className="text-xs px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">breme</span>
                      <input className={konto + (ne ? ' opacity-40' : '')} disabled={ne} maxLength={8} value={n.vrstice[o.kljuc]?.konto || ''} onChange={e => vrst(o.kljuc, 'konto', e.target.value.trim())} />
                      <label className="flex items-center gap-1.5 text-xs text-gray-500 w-32"><input type="checkbox" checked={ne} onChange={e => vrst(o.kljuc, 'ne_prenasa', e.target.checked)} />se ne prenaša{ZVEZDICA[o.kljuc] || ''}</label>
                    </div>);
                })}
                <div className="px-3 py-2.5">
                  <div className="flex items-center gap-3"><div className="flex-1 text-sm">Stroški</div>
                    <span className="text-xs px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">breme</span></div>
                  <div className="mt-2 ml-1 pl-4 border-l-2 border-blue-100 dark:border-blue-500/20 space-y-2">
                    {opis.filter(o => o.kljuc.startsWith('STROSKI_')).map(o => {
                      const ne = !!n.vrstice[o.kljuc]?.ne_prenasa;
                      return (
                        <div key={o.kljuc} className="flex flex-wrap items-center gap-3">
                          <div className="flex-1 min-w-[12rem]"><div className={'text-sm ' + (ne ? 'text-gray-400' : '')}>{o.naziv.replace(/^Stroški – /, '')}</div>
                            {o.kljuc === 'STROSKI_PODJETJE' && !ne && (
                              <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">protiknjižba v dobro
                                <input className={konto} maxLength={8} value={n.vrstice[o.kljuc]?.konto_dobro || ''} placeholder="npr. 220000" onChange={e => vrst(o.kljuc, 'konto_dobro', e.target.value.trim())} /></div>)}</div>
                          <label className="flex items-center gap-1.5 text-xs text-gray-500 w-32"><input type="checkbox" checked={ne} onChange={e => vrst(o.kljuc, 'ne_prenasa', e.target.checked)} />se ne prenaša{ZVEZDICA[o.kljuc] || ''}</label>
                        </div>);
                    })}
                    <div className="pt-1">
                      <div className="text-xs text-gray-500 mb-1">Konto po vrsti stroška (obvezno)</div>
                      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
                        {(VRSTE as any[]).map((x: any) => { const v = n.stroski_vrste?.[x[0]] || ''; return (
                          <label key={x[0]} className="flex items-center justify-between gap-2 text-sm">{x[1]}
                            <input className={konto + (v ? '' : ' !border-red-400')} maxLength={8} placeholder="konto" value={v}
                              onChange={e => setN({ ...n, stroski_vrste: { ...(n.stroski_vrste || {}), [x[0]]: e.target.value.trim() } })} /></label>); })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="text-xs text-gray-500 mt-3 space-y-1">{/* [PATCH pn-prenos-kn21 v3] opombi k »se ne prenaša« */}
                <p>* Znesek se prenese v Evidenco dela in se bo knjižil skozi plače.</p>
                <p>** Podjetje bo najverjetneje prejelo in knjižilo račun — strošek se bo obračunal tam.</p>
                <p>Akontacija se ne knjiži (zapre jo obračun plač). Datum knjiženja = datum prihoda (pri enotni temeljnici ga izbereš ob prenosu).</p>
              </div>
            </section>
          </div>
          <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-4 self-start lg:sticky lg:top-0">
            <div className="text-sm font-medium">Primer knjiženja</div>
            <div className="text-xs text-gray-500 mb-3">Nalog: dnevnice 25,03 · km 43,00 · parkirnina 200,00 (povrne se) · cestnina 5,00 (plačalo podjetje)</div>
            {pr.length === 0 ? <div className="text-sm text-gray-500">Nič za knjižiti — nalog se ob prenosu označi kot prenesen brez knjižb.</div> : <TabelaKnjizb vrstice={pr} />}
          </div>
        </div>)}
    </Okno>);
}

/* [PATCH pn-arhiv-fe v1] Arhiv potnih nalogov: obdobje (mesec / od–do po datumu odhoda), en PDF ali vsak nalog svoj PDF,
   priloge pripete za nalogom; shranjevanje v izbrano mapo (File System Access API — Chrome/Edge; ročaj mape v IndexedDB),
   sicer prenos (vsak svoj PDF = ZIP). Nalogov ne spreminja. */
const ARHIV_DB = 'aicount_pn_arhiv';
function idbMapa(nacin: 'get' | 'put', vrednost?: any): Promise<any> {
  return new Promise(res => {
    try {
      const r = indexedDB.open(ARHIV_DB, 1);
      r.onupgradeneeded = () => r.result.createObjectStore('kv');
      r.onerror = () => res(undefined);
      r.onsuccess = () => {
        try {
          const st = r.result.transaction('kv', nacin === 'get' ? 'readonly' : 'readwrite').objectStore('kv');
          const q = nacin === 'get' ? st.get('mapa') : st.put(vrednost, 'mapa');
          q.onsuccess = () => res(nacin === 'get' ? q.result : undefined);
          q.onerror = () => res(undefined);
        } catch { res(undefined); }
      };
    } catch { res(undefined); }
  });
}
const zadnjiDan = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${ym}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`; };
const prejsnjiMesec = () => { const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
function prenesiBlob(blob: Blob, ime: string) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = ime; document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}
const imeIzGlave = (h: any, privzeto: string) => (String(h?.['content-disposition'] || '').match(/filename="?([^"]+)"?/) || [])[1] || privzeto;

/* [PATCH pn-arhiv-povzetek-fe v1] izbira meseca v slovenščini (brskalnikov <input type="month"> piše v jeziku brskalnika) */
const MESECI_ARHIV = ['januar', 'februar', 'marec', 'april', 'maj', 'junij', 'julij', 'avgust', 'september', 'oktober', 'november', 'december'];
const meseciArhiva = (izbran: string) => { const d = new Date(); const out: string[] = []; for (let i = 0; i < 36; i++) { const x = new Date(d.getFullYear(), d.getMonth() - i, 1); out.push(`${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}`); } if (izbran && !out.includes(izbran)) out.push(izbran); return out; };
const imeMesecaArhiva = (m: string) => { const [l, mm] = m.split('-').map(Number); return `${MESECI_ARHIV[mm - 1] || ''} ${l}`; };

function ArhivOkno({ cid, onZapri }: { cid: string; onZapri: () => void }) {
  const podprto = typeof (window as any).showDirectoryPicker === 'function';
  const [vrsta, setVrsta] = useState<'mesec' | 'obdobje'>('mesec');
  const [mesec, setMesec] = useState(prejsnjiMesec());
  const [od, setOd] = useState('');
  const [doD, setDoD] = useState('');
  const [nacin, setNacin] = useState<'en' | 'vsak'>('en');
  const [seznam, setSeznam] = useState<any[] | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [mapa, setMapa] = useState<any>(null);
  const [delam, setDelam] = useState(false);
  const [napredek, setNapredek] = useState('');
  const obd = vrsta === 'mesec' ? (mesec ? { od: `${mesec}-01`, do: zadnjiDan(mesec) } : null) : (od && doD ? { od, do: doD } : null);
  useEffect(() => { if (podprto) idbMapa('get').then(h => { if (h) setMapa(h); }); }, [podprto]);
  useEffect(() => {
    setSeznam(null); setNapaka(null);
    if (!obd) return;
    if (obd.do < obd.od) { setNapaka('Datum do je pred datumom od'); return; }
    api.get(`/potni-nalogi/${cid}/prenos/arhiv/seznam`, { params: obd }).then(r => setSeznam(r.data))
      .catch(e => setNapaka(e?.response?.data?.detail || 'Napaka pri nalaganju'));
  }, [cid, obd?.od, obd?.do]);

  const izberiMapo = async () => {
    try { const h = await (window as any).showDirectoryPicker({ id: 'pn-arhiv', mode: 'readwrite' }); setMapa(h); await idbMapa('put', h); }
    catch (e: any) { if (e?.name !== 'AbortError') toast.error('Mape ni bilo mogoče izbrati'); }
  };
  const pisiVMapo = async (ime: string, blob: Blob) => {
    const fh = await mapa.getFileHandle(ime, { create: true });
    const w = await fh.createWritable(); await w.write(blob); await w.close();
  };
  const zazeni = async () => {
    if (!obd || !seznam?.length) return;
    if (mapa) { // dovoljenje mora biti zahtevano neposredno ob kliku (pred klici API)
      let p = await mapa.queryPermission?.({ mode: 'readwrite' });
      if (p !== 'granted') p = await mapa.requestPermission?.({ mode: 'readwrite' });
      if (p !== 'granted') { toast.error('Brskalnik ni dovolil pisanja v mapo — izberi mapo znova'); return; }
    }
    setDelam(true);
    let presk = 0;
    try {
      if (nacin === 'en' || !mapa) {
        setNapredek('Pripravljam …');
        const r = await api.post(`/potni-nalogi/${cid}/prenos/arhiv/${nacin === 'en' ? 'pdf' : 'zip'}`, obd, { responseType: 'blob' });
        presk = Number(r.headers['x-preskocene'] || 0);
        const ime = imeIzGlave(r.headers, `PN_arhiv_${obd.od}_${obd.do}.${nacin === 'en' ? 'pdf' : 'zip'}`);
        if (mapa) await pisiVMapo(ime, r.data); else prenesiBlob(r.data, ime);
      } else {
        for (let i = 0; i < seznam.length; i++) {
          const v = seznam[i];
          setNapredek(`${i + 1} / ${seznam.length}`);
          const r = await api.get(`/potni-nalogi/${cid}/prenos/arhiv/nalog/${v.id}`, { responseType: 'blob' });
          presk += Number(r.headers['x-preskocene'] || 0);
          await pisiVMapo(v.datoteka, r.data);
        }
      }
      toast.success(`Arhivirano: ${seznam.length} nalogov` + (mapa ? ` v mapo »${mapa.name}«` : '') + (presk ? ` · prilog ni bilo mogoče pripeti: ${presk}` : ''));
      onZapri();
    } catch (e: any) {
      const d = await napakaIzBloba(e);
      toast.error(d?.detail || 'Arhiviranje ni uspelo');
    } finally { setDelam(false); setNapredek(''); }
  };

  const nPrekl = (seznam || []).filter(v => v.status === 'preklican').length;
  const nPril = (seznam || []).reduce((s, v) => s + Number(v.prilog || 0), 0);
  const noga = (<>
    <button className={gumbSek} onClick={onZapri} disabled={delam}>Prekliči</button>
    <button className={gumb + ' bg-blue-600 text-white'} disabled={delam || !seznam?.length} onClick={zazeni}>
      <Archive className="w-4 h-4" />{delam ? (napredek || 'Delam …') : (mapa ? 'Shrani v mapo' : 'Prenesi')}</button></>);
  return (
    <Okno naslov="Arhiviraj potne naloge" onZapri={() => { if (!delam) onZapri(); }} noga={noga}>
      <div className="space-y-4 text-sm">
        <div>
          <div className="flex gap-2 mb-2">
            <button type="button" className={tabBtn(vrsta === 'mesec')} onClick={() => setVrsta('mesec')}>Mesec</button>
            <button type="button" className={tabBtn(vrsta === 'obdobje')} onClick={() => setVrsta('obdobje')}>Obdobje od – do</button>
          </div>
          {vrsta === 'mesec'
            ? <select className={polje + ' max-w-[12rem]'} value={mesec} onChange={e => setMesec(e.target.value)}>{meseciArhiva(mesec).map(m => <option key={m} value={m}>{imeMesecaArhiva(m)}</option>)}</select>
            : <div className="flex gap-2"><input type="date" className={polje} value={od} onChange={e => setOd(e.target.value)} />
                <input type="date" className={polje} value={doD} onChange={e => setDoD(e.target.value)} /></div>}
          <div className="text-xs text-gray-500 mt-1">Po datumu odhoda. Vključeni so nalogi vseh stanj (tudi odprti in preklicani).</div>
        </div>
        <div className={'p-3 rounded-xl ' + (napaka ? 'bg-red-50 text-red-700' : 'bg-gray-50 dark:bg-gray-800')}>
          {napaka || (seznam === null ? (obd ? 'Nalagam …' : 'Izberi obdobje.')
            : seznam.length ? `${seznam.length} nalogov · preklicanih ${nPrekl} · prilog ${nPril}` : 'V obdobju ni potnih nalogov.')}
        </div>
        <div className="space-y-1">
          <label className="flex items-center gap-2"><input type="radio" checked={nacin === 'en'} onChange={() => setNacin('en')} />Vsi nalogi v en PDF <span className="text-xs text-gray-500">— po zaposlenih, na začetku povzetek</span></label>
          <label className="flex items-center gap-2"><input type="radio" checked={nacin === 'vsak'} onChange={() => setNacin('vsak')} />Vsak nalog v svoj PDF</label>
          <div className="text-xs text-gray-500">Priloge (PDF in slike) so pripete takoj za svojim nalogom.</div>
        </div>
        <div>
          {podprto ? (<>
            <div className="flex items-center gap-2 flex-wrap">
              <button type="button" className={gumbSek} onClick={izberiMapo} disabled={delam}><FolderOpen className="w-4 h-4" />{mapa ? 'Zamenjaj mapo' : 'Izberi mapo'}</button>
              {mapa && <span className="font-medium">📁 {mapa.name}</span>}
            </div>
            <div className="text-xs text-gray-500 mt-1">Brskalnik si mapo zapomni; ob prvem shranjevanju v seji vpraša za dovoljenje. Datoteka z istim imenom se prepiše. Brez izbrane mape se datoteka prenese v Prenose.</div>
          </>) : <div className="text-xs text-gray-500">Ta brskalnik ne omogoča izbire mape — datoteka se prenese v Prenose (pri »vsak nalog v svoj PDF« kot ZIP). Izbira mape deluje v Chrome in Edge.</div>}
        </div>
        <div className="text-xs text-gray-500">Arhiviranje nalogov ne spreminja.</div>
      </div>
    </Okno>);
}

export function PnPrenosPage() {
  const { selectedCompany } = useAuthStore();
  const cid = (selectedCompany as any)?.id as string | undefined;
  const [stanje, setStanje] = useState<'neprenesen' | 'prenesen'>('neprenesen');
  const [vrstice, setVrstice] = useState<any[]>([]);
  const [izbrani, setIzbrani] = useState<Set<string>>(new Set());
  const [tudiOdprti, setTudiOdprti] = useState(false); /* [PATCH pn-prenos-odprti-fe v1] odprti nalogi se prenesejo samo s kljukico */
  const [od, setOd] = useState('');
  const [doD, setDoD] = useState('');
  const [stOd, setStOd] = useState('');
  const [stDo, setStDo] = useState('');
  const [zap, setZap] = useState('');
  const [nalagam, setNalagam] = useState(false);
  const [delam, setDelam] = useState(false);
  const [napaka, setNapaka] = useState<string | null>(null);
  const [pred, setPred] = useState<any>(null);
  const [okno, setOkno] = useState<null | 'nastavitve' | 'prenos' | 'odznaci' | 'arhiv'>(null); // [PATCH pn-arhiv-fe v1] + arhiv
  const [ime, setIme] = useState('');
  const [datumKnj, setDatumKnj] = useState('');

  const nalozi = () => {
    if (!cid) return;
    setNalagam(true); setIzbrani(new Set()); setPred(null);
    const p = new URLSearchParams({ stanje });
    if (od) p.set('od', od);
    if (doD) p.set('do', doD);
    api.get(`/potni-nalogi/${cid}/prenos/seznam?${p.toString()}`).then(r => { setVrstice(r.data); setNapaka(null); })
      .catch(e => setNapaka(e?.response?.status === 403 ? 'Prenos je na voljo samo administratorju podjetja.' : (e?.response?.data?.detail || 'Napaka pri nalaganju')))
      .finally(() => setNalagam(false));
  };
  useEffect(nalozi, [cid, stanje, od, doD]);

  const zaposleni = useMemo(() => Array.from(new Set(vrstice.map(v => v.zaposleni).filter(Boolean))).sort(), [vrstice]);
  const prikaz = vrstice.filter(v => {
    if (zap && v.zaposleni !== zap) return false;
    const s = stNum(v.stevilka), a = stNum(stOd), b = stNum(stDo);
    if (!isNaN(a) && !(s >= a)) return false;
    if (!isNaN(b) && !(s <= b)) return false;
    return true;
  });
  const izbirljiv = (v: any) => stanje !== 'neprenesen' || v.status === 'zakljucen' || tudiOdprti; /* [PATCH pn-prenos-odprti-fe v1] */
  const vsiIzbrani = prikaz.filter(izbirljiv).length > 0 && prikaz.filter(izbirljiv).every(v => izbrani.has(v.id));
  const preklopi = (id: string) => { const v = prikaz.find((x: any) => x.id === id); if (v && !izbirljiv(v)) { toast('Nalog je odprt — za prenos obkljukaj »Prenesi tudi odprte naloge«'); return; }
    setIzbrani(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const sestej = (k: string) => prikaz.filter(v => izbrani.has(v.id)).reduce((s, v) => s + Number(v[k] || 0), 0);

  const predogled = async (odpriPrenos = false) => {
    setDelam(true);
    try {
      const r = await api.post(`/potni-nalogi/${cid}/prenos/predogled`, { ids: Array.from(izbrani) });
      setPred(r.data);
      if (r.data.enotna) { setIme(r.data.enotna.ime_predlog); setDatumKnj(r.data.enotna.datum_predlog); }
      if (odpriPrenos) setOkno('prenos');
    } catch (e: any) { toast.error(e?.response?.data?.detail || 'Predogled ni uspel'); }
    finally { setDelam(false); }
  };
  const prenesi = async () => {
    if (pred?.enotna && !(ime.trim().length >= 1 && ime.trim().length <= 14)) { toast.error('Ime temeljnice: 1–14 znakov'); return; }
    setDelam(true);
    try {
      const telo: any = { ids: Array.from(izbrani) };
      if (pred?.enotna) { telo.ime = ime.trim(); telo.datum = datumKnj || null; }
      const r = await api.post(`/potni-nalogi/${cid}/prenos/kn21`, telo, { responseType: 'blob' });
      const ct = String(r.headers['content-type'] || '');
      if (ct.includes('application/json')) {
        const d = JSON.parse(await (r.data as Blob).text());
        toast.success(`Označeno kot preneseno brez knjižb: ${d.prazni}`);
      } else {
        const cd = String(r.headers['content-disposition'] || '');
        const fn = (cd.match(/filename="?([^"]+)"?/) || [])[1] || 'PN_KN21.xml';
        const url = window.URL.createObjectURL(new Blob([r.data]));
        const a = document.createElement('a'); a.href = url; a.download = fn; document.body.appendChild(a); a.click(); a.remove();
        window.URL.revokeObjectURL(url);
        const prazni = Number(r.headers['x-prazni'] || 0), presk = Number(r.headers['x-preskoceni'] || 0);
        toast.success(`Preneseno: ${r.headers['x-preneseni'] || ''} nalogov` + (prazni ? ` · brez knjižb: ${prazni}` : '') + (presk ? ` · preskočeno: ${presk}` : ''));
      }
      setOkno(null);
      nalozi();
    } catch (e: any) {
      const d = await napakaIzBloba(e);
      toast.error(d?.sporocilo || d?.detail || 'Prenos ni uspel');
    } finally { setDelam(false); }
  };
  const odznaci = async () => {
    setDelam(true);
    try { const r = await api.post(`/potni-nalogi/${cid}/prenos/odznaci`, { ids: Array.from(izbrani) }); toast.success(`Odznačeno: ${r.data.odznaceni}`); setOkno(null); nalozi(); }
    catch (e: any) { toast.error(e?.response?.data?.detail || 'Odznačitev ni uspela'); }
    finally { setDelam(false); }
  };

  if (!cid) return <div className="p-6 text-sm text-gray-500">Izberi podjetje.</div>;
  const nOk = pred?.nalogi?.length || 0, nPrazni = pred?.prazni?.length || 0, nPresk = pred?.preskoceni?.length || 0;
  return (
    <div className="p-4 md:p-6 w-full space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <ArrowRightLeft className="w-5 h-5 text-blue-600" /><h1 className="text-xl font-semibold">Prenos potnih nalogov</h1>
        <div className="flex-1" />
        {!napaka && <button className={gumbSek} onClick={() => setOkno('arhiv')}><Archive className="w-4 h-4" />Arhiviraj</button>}{/* [PATCH pn-arhiv-fe v1] */}
        {okno === 'arhiv' && <ArhivOkno cid={cid} onZapri={() => setOkno(null)} />}
        {!napaka && <button className={gumbSek} onClick={() => setOkno('nastavitve')}><Settings className="w-4 h-4" />Nastavitve prenosa</button>}
      </div>
      {napaka && <div className="p-3 rounded-xl bg-red-50 text-red-700 text-sm">{napaka}</div>}

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 flex flex-wrap items-end gap-3">
        <div className="flex gap-2">
          <button onClick={() => setStanje('neprenesen')} className={tabBtn(stanje === 'neprenesen')}>Neprenešeni</button>
          <button onClick={() => setStanje('prenesen')} className={tabBtn(stanje === 'prenesen')}>Preneseni</button>
        </div>
        <label className="text-xs text-gray-500 w-40">Prihod od<input type="date" className={polje} value={od} onChange={e => setOd(e.target.value)} /></label>
        <label className="text-xs text-gray-500 w-40">Prihod do<input type="date" className={polje} value={doD} onChange={e => setDoD(e.target.value)} /></label>
        <label className="text-xs text-gray-500 w-28">Številka od<input className={polje} inputMode="numeric" placeholder="npr. 1" value={stOd} onChange={e => setStOd(e.target.value)} /></label>
        <label className="text-xs text-gray-500 w-28">Številka do<input className={polje} inputMode="numeric" placeholder="npr. 20" value={stDo} onChange={e => setStDo(e.target.value)} /></label>
        <label className="text-xs text-gray-500 w-56">Zaposleni
          <select className={polje} value={zap} onChange={e => setZap(e.target.value)}><option value="">vsi</option>{zaposleni.map(z => <option key={z} value={z}>{z}</option>)}</select></label>
        <button onClick={nalozi} title="Osveži" className={gumbSek + ' !px-3'}><RefreshCw className="w-4 h-4" /></button>
        <div className="flex-1" />
        {stanje === 'neprenesen' ? (<>
          <button onClick={() => predogled(false)} disabled={delam || !izbrani.size} className={gumbSek}><Eye className="w-4 h-4" />Predogled knjižb</button>
          <button onClick={() => predogled(true)} disabled={delam || !izbrani.size} className={gumb + ' bg-blue-600 text-white'}><Download className="w-4 h-4" />Prenesi (KN-21)</button>
        </>) : (
          <button onClick={() => setOkno('odznaci')} disabled={delam || !izbrani.size} className={gumb + ' bg-amber-600 text-white'}><RotateCcw className="w-4 h-4" />Označi neprenešeno</button>)}
      </div>
      {stanje === 'neprenesen' && <div className="flex items-center gap-4 flex-wrap -mt-2">{/* [PATCH pn-prenos-odprti-fe v1] */}
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={tudiOdprti} onChange={e => { setTudiOdprti(e.target.checked); if (!e.target.checked) setIzbrani(new Set()); }} />Prenesi tudi odprte naloge</label>
        <p className="text-xs text-gray-500">Odprti nalogi so označeni »odprt«; brez kljukice se prenesejo samo zaključeni. Prenesen nalog je zaklenjen (urejanje in preklic samo po odznačitvi).</p></div>}

      <div className="overflow-x-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
        <table className="w-full text-sm">
          <thead><tr className="text-xs text-gray-500 border-b border-gray-200 dark:border-gray-700">
            <th className="px-3 py-2.5 text-left w-10"><input type="checkbox" checked={vsiIzbrani} onChange={() => setIzbrani(vsiIzbrani ? new Set() : new Set(prikaz.filter(izbirljiv).map(v => v.id)))} /></th>
            <th className="px-3 text-left">Številka</th><th className="px-3 text-left">Zaposleni</th><th className="px-3 text-left">Prihod</th>
            <th className="px-3 text-right">Dnevnice</th><th className="px-3 text-right">Kilometrina</th><th className="px-3 text-right">Stroški (povrne)</th>
            <th className="px-3 text-right">Stroški (podjetje)</th><th className="px-3 text-right">Akontacija</th>
            {stanje === 'prenesen' && <th className="px-3 text-left">Preneseno</th>}
          </tr></thead>
          <tbody>
            {nalagam && <tr><td colSpan={10} className="py-10 text-center text-gray-400">Nalagam …</td></tr>}
            {!nalagam && prikaz.length === 0 && <tr><td colSpan={10} className="py-10 text-center text-gray-400">{stanje === 'neprenesen' ? 'Ni neprenešenih nalogov.' : 'Ni prenesenih nalogov.'}</td></tr>}
            {!nalagam && prikaz.map(v => (
              <tr key={v.id} className={'border-b border-gray-100 dark:border-gray-800 cursor-pointer ' + (izbrani.has(v.id) ? 'bg-blue-50/60 dark:bg-blue-500/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40')} onClick={() => preklopi(v.id)}>
                <td className="px-3 py-2"><input type="checkbox" checked={izbrani.has(v.id)} disabled={!izbirljiv(v)} onChange={() => preklopi(v.id)} onClick={e => e.stopPropagation()} /></td>
                <td className="px-3 font-medium whitespace-nowrap">{v.stevilka}{stanje === 'neprenesen' && v.status !== 'zakljucen' && <span className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded-md bg-blue-50 text-blue-700 dark:bg-blue-500/10 dark:text-blue-300">odprt</span>}</td><td className="px-3">{v.zaposleni}</td><td className="px-3 whitespace-nowrap">{datum(v.prihod)}</td>
                <td className="px-3 text-right tabular-nums">{eur(v.dnevnice)}{v.dnevnice_valuta && v.dnevnice_valuta !== 'EUR' ? ' ' + v.dnevnice_valuta : ''}</td>
                <td className="px-3 text-right tabular-nums">{eur(v.kilometrina)}</td><td className="px-3 text-right tabular-nums">{eur(v.stroski_povrne)}</td>
                <td className="px-3 text-right tabular-nums">{eur(v.stroski_podjetje)}</td><td className="px-3 text-right tabular-nums">{eur(v.akontacija)}</td>
                {stanje === 'prenesen' && <td className="px-3 text-xs text-gray-500 whitespace-nowrap">{datum(v.exported_at)} {(v.exported_at || '').slice(11, 16)} · {v.export_format === 'brez_knjizb' ? 'brez knjižb' : 'KN-21'}</td>}
              </tr>))}
          </tbody>
          {izbrani.size > 0 && (
            <tfoot><tr className="text-xs font-medium text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/40">
              <td className="px-3 py-2" colSpan={4}>Izbrano: {izbrani.size}</td>
              {['dnevnice', 'kilometrina', 'stroski_povrne', 'stroski_podjetje', 'akontacija'].map(k => <td key={k} className="px-3 text-right tabular-nums">{eur(sestej(k))}</td>)}
              {stanje === 'prenesen' && <td />}
            </tr></tfoot>)}
        </table>
      </div>

      {pred && okno !== 'prenos' && (
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm font-semibold">Predogled knjižb · simbol {pred.simbol}{pred.enotna ? ' · ena temeljnica' : ''}</div>
            <button className="text-gray-400 hover:text-gray-600" aria-label="Zapri predogled" onClick={() => setPred(null)}><X className="w-4 h-4" /></button>
          </div>
          {(pred.preskoceni || []).map((p: any) => (
            <div key={p.id} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300"><AlertTriangle className="w-4 h-4" />{p.stevilka || p.id}: {p.razlog} — ne bo prenesen</div>))}
          {nPrazni > 0 && <div className="text-sm text-gray-500 mt-1">Brez knjižb (označeni bodo kot preneseni): {pred.prazni.map((p: any) => p.stevilka).join(', ')}</div>}
          {pred.enotna ? (
            <div className="mt-3 rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3 max-w-3xl">
              <div className="text-sm font-medium mb-1">Temeljnica {pred.enotna.ime_predlog} · {nOk} nalogov</div><TabelaKnjizb vrstice={pred.enotna.vrstice} />
            </div>) : (
            <div className="grid gap-3 mt-3 md:grid-cols-2 2xl:grid-cols-3">
              {(pred.nalogi || []).map((n: any) => (
                <div key={n.id} className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3">
                  <div className="text-sm font-medium mb-1">{n.stevilka}</div><TabelaKnjizb vrstice={n.vrstice} />
                </div>))}
            </div>)}
        </div>)}

      {okno === 'nastavitve' && <NastavitveOkno cid={cid} onZapri={() => { setOkno(null); setPred(null); }} />}

      {okno === 'prenos' && pred && (
        <Okno naslov="Prenos v Vasco (KN-21)" onZapri={() => setOkno(null)} noga={<>
          <button className={gumbSek} onClick={() => setOkno(null)}>Prekliči</button>
          <button className={gumb + ' bg-blue-600 text-white'} disabled={delam || (nOk + nPrazni === 0)} onClick={prenesi}><Download className="w-4 h-4" />Prenesi</button></>}>
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-3 gap-2">
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3"><div className="text-xs text-gray-500">Z knjižbami</div><div className="text-lg font-semibold">{nOk}</div></div>
              <div className="rounded-xl bg-gray-50 dark:bg-gray-800/50 p-3"><div className="text-xs text-gray-500">Brez knjižb</div><div className="text-lg font-semibold">{nPrazni}</div></div>
              <div className={'rounded-xl p-3 ' + (nPresk ? 'bg-red-50 dark:bg-red-500/10' : 'bg-gray-50 dark:bg-gray-800/50')}><div className="text-xs text-gray-500">Preskočeni</div><div className="text-lg font-semibold">{nPresk}</div></div>
            </div>
            {(pred.preskoceni || []).map((p: any) => <div key={p.id} className="text-xs text-red-700 dark:text-red-300">{p.stevilka || p.id}: {p.razlog}</div>)}
            {pred.enotna && nOk > 0 && (
              <div className="grid grid-cols-2 gap-3">
                <label className="text-xs text-gray-500">Ime temeljnice (do 14 znakov)<input className={polje + ' font-mono'} maxLength={14} value={ime} onChange={e => setIme(e.target.value)} /></label>
                <label className="text-xs text-gray-500">Datum knjiženja<input type="date" className={polje} value={datumKnj} onChange={e => setDatumKnj(e.target.value)} /></label>
              </div>)}
            {nOk + nPrazni === 0 ? <div className="text-gray-500">Ni nalogov, ki bi jih bilo mogoče prenesti.</div>
              : <div className="text-gray-500">{pred.enotna ? `Vsi nalogi bodo v eni temeljnici.` : `Vsak nalog bo svoja temeljnica.`} Preneseni nalogi se zaklenejo.</div>}
          </div>
        </Okno>)}

      {okno === 'odznaci' && (
        <Okno naslov="Označi kot neprenešeno" onZapri={() => setOkno(null)} noga={<>
          <button className={gumbSek} onClick={() => setOkno(null)}>Prekliči</button>
          <button className={gumb + ' bg-amber-600 text-white'} disabled={delam} onClick={odznaci}><RotateCcw className="w-4 h-4" />Označi neprenešeno</button></>}>
          <p className="text-sm">Izbranih nalogov: <b>{izbrani.size}</b>. Vrnili se bodo med neprenešene in bodo spet urejljivi.</p>
          <p className="text-sm text-amber-700 dark:text-amber-300 mt-2 flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />Temeljnico, ki je že uvožena v Vasco, moraš tam po potrebi zbrisati ročno.</p>
        </Okno>)}
    </div>);
}
